(() => {
  class JournalFeatured extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector("[data-journal-track]");
      if (!track) return;
      this.slides = Array.from(track.children);
      this.prev = this.querySelector('[data-dir="-1"]');
      this.next = this.querySelector('[data-dir="1"]');
      this.current = this.querySelector("[data-journal-current]");
      this.index = 0;
      this.loop = this.hasAttribute("data-loop");

      this.addEventListener("click", (event) => {
        const button = event.target.closest("[data-dir]");
        if (!button || button.disabled) return;
        const dir = Number(button.dataset.dir);
        this.goTo(this.index + dir, dir);
      });

      track.addEventListener("pointerdown", (event) => {
        if (event.pointerType === "mouse") return;
        this.swipeX = event.clientX;
      });
      track.addEventListener("pointerup", (event) => {
        if (this.swipeX == null) return;
        const dx = event.clientX - this.swipeX;
        this.swipeX = null;
        const dir = dx < 0 ? 1 : -1;
        if (Math.abs(dx) > 50) this.goTo(this.index + dir, dir);
      });
      track.addEventListener("pointercancel", () => {
        this.swipeX = null;
      });
    }

    // Direction comes from the control, not slide order, so a looped wrap still slides the right way.
    goTo(index, dir) {
      const count = this.slides.length;
      if (this.loop) index = (index + count) % count;
      if (!this.slides[index] || index === this.index) return;

      const outgoing = this.slides[this.index];
      const incoming = this.slides[index];
      incoming.classList.add("is-positioning");
      incoming.classList.toggle("is-before", dir < 0);
      void incoming.offsetWidth;
      incoming.classList.remove("is-positioning");
      outgoing.classList.toggle("is-before", dir > 0);

      // Inactive slides are inert so keyboard users can't tab into them.
      this.index = index;
      this.slides.forEach((slide) => {
        slide.classList.toggle("is-active", slide === incoming);
        slide.inert = slide !== incoming;
      });

      if (this.current) this.current.textContent = index + 1;
      if (this.loop) return;
      const focused = document.activeElement;
      this.prev.disabled = index === 0;
      this.next.disabled = index === count - 1;
      if (focused?.disabled) (focused === this.prev ? this.next : this.prev).focus();
    }
  }

  class JournalListing extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.blogPath = new URL(this.dataset.blogUrl, location.origin).pathname;
      this.status = this.querySelector("[data-journal-status]");
      this.tagsOpen = this.querySelector("[data-journal-toggle]")?.getAttribute("aria-expanded") === "true";

      this.addEventListener("click", this.onClick.bind(this));
      this.onPopState = this.onPopState.bind(this);
      window.addEventListener("popstate", this.onPopState);
      this.observeMore();
      this.initSearch();

      // Webfonts change segment widths, so wait for them before measuring.
      this.centerCategory = this.centerCategory.bind(this);
      if (document.readyState === "complete") this.centerCategory();
      else window.addEventListener("load", this.centerCategory, { once: true });
    }

    disconnectedCallback() {
      window.removeEventListener("popstate", this.onPopState);
      window.removeEventListener("load", this.centerCategory);
      this.moreObserver?.disconnect();
    }

    setTagsOpen(open) {
      const toggle = this.querySelector("[data-journal-toggle]");
      const tags = this.querySelector("[data-journal-tags]");
      if (!toggle || !tags) return;
      toggle.setAttribute("aria-expanded", open);
      tags.hidden = !open;
    }

    centerCategory() {
      const list = this.querySelector("[data-journal-categories]");
      const active = list?.querySelector(".is-active");
      if (!active || list.scrollWidth <= list.clientWidth) return;
      const target = active.offsetLeft - (list.clientWidth - active.offsetWidth) / 2;
      list.scrollLeft = Math.max(0, Math.min(list.scrollWidth - list.clientWidth, target));
    }

    initSearch() {
      this.searchForm = this.querySelector("[data-journal-search]");
      if (!this.searchForm) return;
      this.searchInput = this.searchForm.querySelector("[data-journal-search-input]");

      this.searchForm.addEventListener("submit", (event) => {
        event.preventDefault();
        clearTimeout(this.searchTimer);
        this.search("push");
      });
      this.searchInput.addEventListener("input", () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.search("replace"), 300);
      });

      // The blog page can't read ?q= in Liquid, so a reload or shared link runs the search here.
      const query = new URLSearchParams(location.search).get("q");
      if (query) {
        this.searchInput.value = query;
        this.applyFilter(location.href, null, null);
      }
    }

    search(mode) {
      const url = this.withQuery(location.href);
      url.searchParams.delete("page");
      if (url.href !== location.href) this.applyFilter(url.href, null, mode);
    }

    withQuery(href) {
      const url = new URL(href, location.origin);
      url.hash = "";
      const query = this.searchInput?.value.trim();
      if (query) url.searchParams.set("q", query);
      else url.searchParams.delete("q");
      return url;
    }

    // Shopify search can't scope to a blog URL, so the active /tagged/ handles become tag: terms.
    searchUrl(href, query) {
      const tagged = new URL(href, location.origin).pathname.split("/tagged/")[1];
      const tags = tagged ? tagged.split("+").map((handle) => ` AND tag:${decodeURIComponent(handle)}`) : [];
      const url = new URL(this.dataset.searchUrl, location.origin);
      url.searchParams.set("q", `(${query})${tags.join("")}`);
      url.searchParams.set("type", "article");
      url.searchParams.set("section_id", this.dataset.searchSection);
      return url.href;
    }

    showSearchResults(doc, query) {
      const grid = doc.querySelector("[data-journal-grid]");
      let replacement = grid;
      if (!grid?.children.length) {
        replacement = document.createElement("p");
        replacement.className = "journal-listing__empty";
        replacement.textContent = this.searchForm.dataset.emptyText;
      }
      this.results.querySelector("[data-journal-grid], .journal-listing__empty")?.replaceWith(replacement);
      this.results.querySelector("[data-journal-pagination]")?.replaceWith(doc.querySelector("[data-journal-pagination]"));
      this.results.querySelector("[data-journal-count-value]")?.replaceWith(doc.querySelector("[data-journal-count-value]"));
      const echo = this.results.querySelector("[data-journal-count-query]");
      if (echo) echo.textContent = `for “${query}”`;
    }

    get results() {
      return this.querySelector("[data-journal-results]");
    }

    onClick(event) {
      const toggle = event.target.closest("[data-journal-toggle]");
      if (toggle) {
        this.tagsOpen = toggle.getAttribute("aria-expanded") !== "true";
        this.setTagsOpen(this.tagsOpen);
        return;
      }

      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;

      const filter = event.target.closest("a[data-journal-filter]");
      if (filter) {
        event.preventDefault();
        if (filter.closest("[data-journal-categories]") && filter.getAttribute("aria-current") === "page") return;
        this.applyFilter(this.withQuery(filter.href).href, filter.dataset.journalFilter, "push");
        return;
      }

      const more = event.target.closest("a[data-journal-more]");
      if (more) {
        event.preventDefault();
        this.loadMore(more);
        return;
      }

      const page = event.target.closest("[data-journal-pagination] .pagination a[href]");
      if (page) {
        event.preventDefault();
        // The #JournalListing anchor is only for no-JS page loads; keep it out of the address bar.
        const url = new URL(page.href);
        url.hash = "";
        if (url.searchParams.get("page") === "1") url.searchParams.delete("page");
        this.applyFilter(url.href, null, "push").then(() => {
          this.scrollIntoView({ behavior: "smooth", block: "start" });
          this.querySelector(".journal-card__title a")?.focus({ preventScroll: true });
        });
      }
    }

    onPopState() {
      if (!location.pathname.startsWith(this.blogPath)) return;
      if (this.searchInput) this.searchInput.value = new URLSearchParams(location.search).get("q") || "";
      this.applyFilter(location.href, null, null);
    }

    async fetchSection(url, signal) {
      const request = new URL(url, location.origin);
      if (!request.searchParams.has("section_id")) request.searchParams.set("section_id", this.sectionId);
      const response = await fetch(request, { signal });
      if (!response.ok) throw new Error(`Journal request failed: ${response.status}`);
      const html = await response.text();
      return new DOMParser().parseFromString(html, "text/html");
    }

    // mode is "push", "replace" or null (popstate, initial search).
    async applyFilter(url, handle, mode) {
      this.filterController?.abort();
      this.filterController = new AbortController();
      const { signal } = this.filterController;
      this.setAttribute("aria-busy", "true");
      const query = new URL(url, location.origin).searchParams.get("q")?.trim();

      try {
        const [doc, found] = await Promise.all([
          this.fetchSection(url, signal),
          query && this.searchForm ? this.fetchSection(this.searchUrl(url, query), signal) : null,
        ]);
        const next = doc.querySelector("[data-journal-results]");
        if (!next) throw new Error("Journal results missing from response");

        this.results.innerHTML = next.innerHTML;
        if (found) this.showSearchResults(found, query);
        if (this.tagsOpen) this.setTagsOpen(true);
        this.centerCategory();
        if (mode === "push") history.pushState({ journal: true }, "", url);
        if (mode === "replace") history.replaceState({ journal: true }, "", url);
        this.announce();
        this.observeMore();

        if (handle) {
          this.querySelector(`[data-journal-filter="${CSS.escape(handle)}"]`)?.focus({
            preventScroll: true,
          });
        }
      } catch (error) {
        if (error.name === "AbortError") return;
        // Reloading a ?q= URL reruns the same search on load, so a failing search would loop.
        if (query) return;
        location.assign(url);
      } finally {
        this.removeAttribute("aria-busy");
      }
    }

    async loadMore(link) {
      if (this.loadingMore) return;
      this.loadingMore = true;
      link.setAttribute("aria-busy", "true");
      const controller = this.filterController;

      try {
        const doc = await this.fetchSection(link.href);
        if (controller !== this.filterController) return;

        const grid = this.querySelector("[data-journal-grid]");
        const nextGrid = doc.querySelector("[data-journal-grid]");
        const pagination = this.querySelector("[data-journal-pagination]");
        const nextPagination = doc.querySelector("[data-journal-pagination]");
        if (!grid || !nextGrid || !pagination || !nextPagination) {
          throw new Error("Journal page missing from response");
        }

        const firstNew = nextGrid.firstElementChild;
        const hadFocus = document.activeElement === link;
        grid.append(...nextGrid.children);
        pagination.replaceWith(nextPagination);
        this.observeMore();

        if (hadFocus) {
          firstNew?.querySelector(".journal-card__title a")?.focus({ preventScroll: true });
        }
      } catch (error) {
        location.assign(link.href);
      } finally {
        this.loadingMore = false;
        link.removeAttribute("aria-busy");
      }
    }

    // Infinite scroll; the Load More link stays as the fallback on slow connections.
    observeMore() {
      this.moreObserver?.disconnect();
      const link = this.querySelector("[data-journal-more]");
      if (!link) return;
      this.moreObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadMore(link);
        },
        { rootMargin: "0px 0px 400px 0px" }
      );
      this.moreObserver.observe(link);
    }

    announce() {
      const count = this.querySelector("[data-journal-count]");
      if (this.status && count) this.status.textContent = count.textContent;
    }
  }

  if (!customElements.get("journal-featured")) {
    customElements.define("journal-featured", JournalFeatured);
  }
  if (!customElements.get("journal-listing")) {
    customElements.define("journal-listing", JournalListing);
  }
})();
